import { PillProps } from "@validereinc/common-components";
import {
  WorkerRunStatus,
  type WorkerRunType,
  WorkerVersionStatus,
  WorkerVersionType,
} from "@validereinc/domain";
import { timeDurationToTimeUnits } from "@validereinc/utilities";
import { differenceInMilliseconds, parseISO } from "date-fns";

/** Visible prefix length for worker run UUIDs in UI (tables, titles, overview). */
export const WORKER_RUN_ID_DISPLAY_PREFIX_LENGTH = 7;

/**
 * Returns the first {@link WORKER_RUN_ID_DISPLAY_PREFIX_LENGTH} characters of a worker run id for display.
 */
export const getWorkerRunIdDisplayLabel = (workerRunId: string): string =>
  workerRunId.slice(0, WORKER_RUN_ID_DISPLAY_PREFIX_LENGTH);

/**
 * Formats wall-clock duration from {@link WorkerRunType.started_at} to
 * {@link WorkerRunType.finished_at} as `HH:MM:SS`, or an em dash while the run
 * is still in progress or has no finish timestamp.
 */
export const formatWorkerRunDuration = (run: WorkerRunType): string => {
  if (run.status === WorkerRunStatus.STARTED) return "—";
  if (run.finished_at == null || String(run.finished_at).trim() === "") {
    return "—";
  }

  return timeDurationToTimeUnits(
    Math.max(
      0,
      differenceInMilliseconds(
        parseISO(run.finished_at),
        parseISO(run.started_at)
      )
    ),
    "seconds"
  )
    .map((i) => `${i.value} ${i.unit}`)
    .join(", ");
};

export const WorkerVersionStatusToPillVariantMapping: Record<
  WorkerVersionType["status"],
  PillProps["variant"]
> = {
  [WorkerVersionStatus.PENDING]: "warning",
  [WorkerVersionStatus.ACTIVE]: "success",
  [WorkerVersionStatus.FAILED]: "error",
  [WorkerVersionStatus.ARCHIVED]: "default",
};

export const WorkerRunStatusToPillVariantMapping: Record<
  WorkerRunType["status"],
  PillProps["variant"]
> = {
  [WorkerRunStatus.STARTED]: "info",
  [WorkerRunStatus.COMPLETED]: "success",
  [WorkerRunStatus.FAILED]: "error",
};
